import { endOfMonth, startOfMonth } from 'date-fns';
import { useMemo, useState } from 'react';
import { Spinner } from '@components/Spinner';
import { Calendar } from '@shared/components/calendar/Calendar';
import { centsToWholeDollar } from '@shared/utils/currency';
import { toISODateFormat } from '@shared/utils/dateFormatters';
import { useTemporalListingPriceForecast } from './hooks';
import type { DailyPriceForecast } from './types';
import styles from './ForecastCalendar.scss';

const ForecastDayLabel = ({ forecast }: { forecast?: DailyPriceForecast }) => {
  if (!forecast || (forecast.minPrice == null && forecast.maxPrice == null)) {
    return <span className={styles.priceLabel}>N/A</span>;
  }

  return (
    <span className={styles.priceLabel}>
      {`${centsToWholeDollar(forecast.minPrice ?? undefined)} / ${centsToWholeDollar(forecast.maxPrice ?? undefined)}`}
    </span>
  );
};

export const ForecastCalendar = () => {
  const [activeStartDate, setActiveStartDate] = useState(() =>
    startOfMonth(new Date()),
  );

  const startDate = useMemo(
    () => toISODateFormat(startOfMonth(activeStartDate)),
    [activeStartDate],
  );
  const endDate = useMemo(
    () => toISODateFormat(endOfMonth(activeStartDate)),
    [activeStartDate],
  );

  const { data: forecast, isLoading } = useTemporalListingPriceForecast(
    startDate,
    endDate,
  );

  const forecastByDate = useMemo(() => {
    const map = new Map<string, DailyPriceForecast>();
    forecast?.forEach((day) => {
      map.set(day.date, day);
    });
    return map;
  }, [forecast]);

  return (
    <div className={styles.forecastCalendar}>
      {isLoading ? (
        <div className={styles.loadingState}>
          <Spinner />
        </div>
      ) : (
        <Calendar
          onDateChange={() => {}}
          tileDisabled={() => true}
          minDate={startOfMonth(new Date())}
          activeStartDate={activeStartDate}
          onActiveStartDateChange={({
            activeStartDate: nextActiveStartDate,
          }) => {
            if (nextActiveStartDate) {
              setActiveStartDate(startOfMonth(nextActiveStartDate));
            }
          }}
          tileContent={({ date }) => (
            <ForecastDayLabel
              forecast={forecastByDate.get(toISODateFormat(date))}
            />
          )}
        />
      )}
    </div>
  );
};
